// components/GridGenerationInlinePanel.tsx
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Loader2, X } from '@/components/ui/lucide-memo';
import { useKernel } from '@/kernel/Kernel';
import { useKernelStore } from '@/kernel/kernelStores';
import { isInertSurface } from '#lib/keyboard-shortcuts';
import Switch from './ui/Switch';
import GridGenerationTilesPreviewOverlay from './GridGenerationTilesPreviewOverlay';
import { useGridGenerationForm } from './useGridGenerationForm';
import { buildLiveScreenX, buildLiveScreenY } from '#lib/layer-geometry';
import {
  GRID_SIZE_MAX,
  GRID_SIZE_MIN,
  TILE_OVERLAP_SCALE_MAX,
  TILE_OVERLAP_SCALE_MIN,
  TILE_OVERLAP_SCALE_STEP,
  GridEdgeFadeRadius,
  GridGenerationPayload,
  GridGenerationRuntime,
  GridHarmonizationMode,
  GridOrder,
  GridTileShape,
  GridSweepMode,
  buildGridPreviewZones,
  parseAndValidateGridSize,
} from '#lib/grid-generation-script';

const translations = {
  en: {
    closePanelAria: 'Close grid generation panel',
    tilePreviewOverlay: 'Tile preview overlay',
    missingWorkspaceId: 'Workspace ID is required to run grid generation.',
  },
  fr: {
    closePanelAria: 'Fermer le panneau de generation en grille',
    tilePreviewOverlay: 'Apercu des tuiles',
    missingWorkspaceId: 'ID workspace requis pour lancer la generation en grille.',
  },
} as const;

const stopEventPropagation = (event: React.SyntheticEvent) => event.stopPropagation();

interface GridGenerationInlinePanelProps {
  projectId: string;
  workspaceId: string;
  payload?: GridGenerationPayload;
  onClose: () => void;
}

const GridGenerationInlinePanel: React.FC<GridGenerationInlinePanelProps> = ({
  projectId,
  workspaceId,
  payload,
  onClose,
}) => {
  const { kernel } = useKernel();
  const lang = useKernelStore((state) => state.language);
  const localT = useMemo(
    () => translations[lang as keyof typeof translations] ?? translations.en,
    [lang]
  );
  const zoomLevel = useKernelStore((state) => {
    const runtime = state.inventory.Project.registry[projectId]?.runtime;
    const value = runtime?.zoomLevel;
    return typeof value === 'number' && Number.isFinite(value) ? value : 1;
  });
  const panOffset = useKernelStore((state) => {
    const runtime = state.inventory.Project.registry[projectId]?.runtime;
    const value = runtime?.panOffset;
    if (
      value &&
      typeof value === 'object' &&
      Number.isFinite((value as { x?: unknown }).x) &&
      Number.isFinite((value as { y?: unknown }).y)
    ) {
      return value as { x: number; y: number };
    }
    return { x: 0, y: 0 };
  });
  const [previewEnabled, setPreviewEnabled] = useState(true);
  const [previewTick, setPreviewTick] = useState(0);

  const resetPreviewToFirstTile = useCallback(() => {
    setPreviewTick(0);
  }, []);

  const resolveInlineWorkspaceId = useCallback(() => {
    const normalizedWorkspaceId = workspaceId.trim();
    if (!normalizedWorkspaceId) {
      throw new Error(localT.missingWorkspaceId);
    }
    return normalizedWorkspaceId;
  }, [workspaceId, localT]);

  const {
    t: sharedT,
    generationRect,
    frameRect,
    gridSizeInput,
    setGridSizeInput,
    includeCenterProbeForEven,
    setIncludeCenterProbeForEven,
    overlapScale,
    setOverlapScale,
    tileHarmonization,
    setTileHarmonization,
    preserveHighRes,
    setPreserveHighRes,
    tileEdgeFadeRadius,
    setTileEdgeFadeRadius,
    tileShape,
    setTileShape,
    order,
    setOrder,
    sweepMode,
    setSweepMode,
    error,
    isSubmitting,
    gridMetrics,
    overlapPercent,
    generationModelId,
    handleSubmit,
  } = useGridGenerationForm({
    projectId,
    workspaceId,
    payload,
    resolveWorkspaceId: resolveInlineWorkspaceId,
    logActionName: 'GA Grid Script Generated and Executed (Inline Panel)',
    onClose,
    onControlChange: resetPreviewToFirstTile,
  });
  const t = useMemo(() => ({ ...sharedT, ...localT }), [sharedT, localT]);

  const handleGridSizeChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => setGridSizeInput(event.target.value),
    [setGridSizeInput]
  );
  const handleSweepModeChange = useCallback(
    (event: React.ChangeEvent<HTMLSelectElement>) => setSweepMode(event.target.value as GridSweepMode),
    [setSweepMode]
  );
  const handleOverlapChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => setOverlapScale(Number.parseFloat(event.target.value)),
    [setOverlapScale]
  );
  const handleHarmonizationChange = useCallback(
    (event: React.ChangeEvent<HTMLSelectElement>) => setTileHarmonization(event.target.value as GridHarmonizationMode),
    [setTileHarmonization]
  );
  const handlePreserveHighResChange = useCallback(
    (checked: boolean) => setPreserveHighRes(checked),
    [setPreserveHighRes]
  );
  const handleTileEdgeFadeChange = useCallback(
    (event: React.ChangeEvent<HTMLSelectElement>) => {
      const value = event.target.value;
      setTileEdgeFadeRadius(value === 'auto' ? 'auto' : Number(value) as GridEdgeFadeRadius);
    },
    [setTileEdgeFadeRadius]
  );
  const handleTileShapeChange = useCallback(
    (event: React.ChangeEvent<HTMLSelectElement>) => setTileShape(event.target.value as GridTileShape),
    [setTileShape]
  );
  const handleOrderChange = useCallback(
    (event: React.ChangeEvent<HTMLSelectElement>) => setOrder(event.target.value as GridOrder),
    [setOrder]
  );
  const handlePreviewEnabledChange = useCallback(
    (checked: boolean) => {
      setPreviewEnabled(checked);
      resetPreviewToFirstTile();
    },
    [resetPreviewToFirstTile]
  );

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || isInertSurface(kernel.current?.canvasContainerRef.current)) return;
      const target = event.target as HTMLElement | null;
      if (target && (target.tagName === 'INPUT' || target.tagName === 'SELECT' || target.tagName === 'TEXTAREA')) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      onClose();
    };
    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, [onClose]);

  const previewRuntime = useMemo<GridGenerationRuntime | null>(() => {
    const gridSize = parseAndValidateGridSize(gridSizeInput);
    if (gridSize === null) return null;
    return {
      gridSize,
      includeCenterProbeForEven,
      clampToFrame: true,
      order,
      sweepMode,
      overlapScale,
      tileHarmonization,
      preserveHighRes,
      tileEdgeFadeRadius,
      tileShape,
    };
  }, [gridSizeInput, includeCenterProbeForEven, order, sweepMode, overlapScale, tileHarmonization, preserveHighRes, tileEdgeFadeRadius, tileShape]);
  const previewZones = useMemo(() => {
    if (!previewRuntime || !generationRect) return [];
    if (!(generationRect.w > 0 && generationRect.h > 0)) return [];
    return buildGridPreviewZones(generationRect, previewRuntime, frameRect);
  }, [previewRuntime, generationRect, frameRect]);
  const previewZoneCount = previewZones.length;
  const previewActiveIndex = previewZoneCount > 0 ? previewTick % previewZoneCount : 0;

  useEffect(() => {
    if (!previewEnabled || previewZoneCount <= 1) return;
    // Slower playback so each tile focus is easier to parse.
    const stepMs = previewZoneCount > 120 ? 320 : previewZoneCount > 60 ? 440 : 560;
    const timer = window.setInterval(() => {
      setPreviewTick((previousTick) => previousTick + 1);
    }, stepMs);
    return () => {
      window.clearInterval(timer);
    };
  }, [previewEnabled, previewZoneCount]);

  const panelStyle = useMemo<React.CSSProperties>(() => {
    if (!generationRect) {
      return { display: 'none' };
    }
    return {
      position: 'absolute',
      left: `calc(${buildLiveScreenX(generationRect.x + generationRect.w, panOffset.x, zoomLevel)} + 12px)`,
      top: buildLiveScreenY(generationRect.y, panOffset.y, zoomLevel),
      width: '320px',
      maxHeight: 'min(640px, calc(100vh - 140px))',
      pointerEvents: 'auto',
      zIndex: 40,
      userSelect: 'none',
    };
  }, [generationRect, panOffset.x, panOffset.y, zoomLevel]);

  if (!generationRect) return null;

  return (
    <div
      style={panelStyle}
      onMouseDown={stopEventPropagation}
      onTouchStart={stopEventPropagation}
      onClick={stopEventPropagation}
      onContextMenu={stopEventPropagation}
    >
      <GridGenerationTilesPreviewOverlay
        projectId={projectId}
        workspaceId={workspaceId}
        runtime={previewRuntime}
        enabled={previewEnabled}
        activeIndex={previewActiveIndex}
      />

      <form
        onSubmit={handleSubmit}
        className="bg-neutral-800/92 backdrop-blur-md text-white rounded-xl shadow-2xl border border-neutral-700/80 overflow-hidden flex flex-col"
      >
        <div className="px-3 py-2 border-b border-neutral-700/80 flex items-center justify-between">
          <div className="min-w-0">
            <p className="text-sm font-semibold text-neutral-100 truncate">{t.title}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-neutral-400 hover:text-white"
            disabled={isSubmitting}
            aria-label={t.closePanelAria}
          >
            <X size={16} />
          </button>
        </div>

        <div className="p-3 overflow-y-auto custom-scrollbar space-y-3">
          <div className="p-2 bg-neutral-700/70 rounded-md">
            <label className="block text-xs text-neutral-200 mb-1" htmlFor="ga-grid-size-inline">
              {t.gridSize}
            </label>
            <input
              id="ga-grid-size-inline"
              type="number"
              min={GRID_SIZE_MIN}
              max={GRID_SIZE_MAX}
              step={1}
              value={gridSizeInput}
              onChange={handleGridSizeChange}
              className="w-full p-2 border border-neutral-600 rounded-md bg-neutral-800 text-white text-sm focus:ring-[#B3D9FF]"
              disabled={isSubmitting}
            />
          </div>

          <div className="p-2 bg-neutral-700/70 rounded-md">
            <label className="block text-xs text-neutral-200 mb-1" htmlFor="ga-tile-shape-inline">
              {t.tileShape}
            </label>
            <select
              id="ga-tile-shape-inline"
              value={tileShape}
              onChange={handleTileShapeChange}
              className="w-full p-2 border border-neutral-600 rounded-md bg-neutral-800 text-white text-sm focus:ring-[#B3D9FF]"
              disabled={isSubmitting}
            >
              <option value="matchFrame">{t.frameTileShape}</option>
              <option value="square">{t.squareTileShape}</option>
            </select>
          </div>

          <div className="p-2 bg-neutral-700/70 rounded-md">
            <label className="block text-xs text-neutral-200 mb-1" htmlFor="ga-sweep-mode-inline">
              {t.sweepMode}
            </label>
            <select
              id="ga-sweep-mode-inline"
              value={sweepMode}
              onChange={handleSweepModeChange}
              className="w-full p-2 border border-neutral-600 rounded-md bg-neutral-800 text-white text-sm focus:ring-[#B3D9FF]"
              disabled={isSubmitting}
            >
              <option value="classicGrid">{t.classicGridSweep}</option>
              <option value="overlapFromCenter">{t.overlapSweepFromCenter}</option>
            </select>
          </div>

          <div className="p-2 bg-neutral-700/70 rounded-md">
            <div className="mb-1 flex items-center justify-between">
              <label className="block text-xs text-neutral-200" htmlFor="ga-overlap-inline">
                {t.tileOverlap}
              </label>
              <span className="text-[11px] text-neutral-300 tabular-nums">{overlapPercent}%</span>
            </div>
            <input
              id="ga-overlap-inline"
              type="range"
              min={TILE_OVERLAP_SCALE_MIN}
              max={TILE_OVERLAP_SCALE_MAX}
              step={TILE_OVERLAP_SCALE_STEP}
              value={overlapScale}
              onChange={handleOverlapChange}
              className="w-full accent-[#B3D9FF]"
              disabled={isSubmitting}
            />
            <p className="mt-1 text-[11px] text-neutral-400">{t.noOverlapHint}</p>
          </div>

          <div className="p-2 bg-neutral-700/70 rounded-md">
            <label className="block text-xs text-neutral-200 mb-1" htmlFor="ga-harmonization-inline">
              {t.tileHarmonization}
            </label>
            <select
              id="ga-harmonization-inline"
              value={tileHarmonization}
              onChange={handleHarmonizationChange}
              className="w-full p-2 border border-neutral-600 rounded-md bg-neutral-800 text-white text-sm focus:ring-[#B3D9FF]"
              disabled={isSubmitting}
            >
              <option value="off">{t.noHarmonization}</option>
              <option value="soft">{t.softHarmonization}</option>
              <option value="strict">{t.strictHarmonization}</option>
            </select>
          </div>

          <div className="flex items-center justify-between p-2 bg-neutral-700/70 rounded-md">
            <span className="text-xs text-neutral-200">{t.preserveHighRes}</span>
            <Switch
              checked={preserveHighRes}
              onCheckedChange={handlePreserveHighResChange}
              labelRight={t.on}
              labelLeft={t.off}
              disabled={isSubmitting}
            />
          </div>

          <div className="p-2 bg-neutral-700/70 rounded-md">
            <label className="block text-xs text-neutral-200 mb-1" htmlFor="ga-edge-fade-inline">
              {t.tileEdgeFade}
            </label>
            <select
              id="ga-edge-fade-inline"
              value={tileEdgeFadeRadius}
              onChange={handleTileEdgeFadeChange}
              className="w-full p-2 border border-neutral-600 rounded-md bg-neutral-800 text-white text-sm focus:ring-[#B3D9FF]"
              disabled={isSubmitting}
            >
              <option value="auto">{t.autoEdgeFade}</option>
              <option value="0">{t.noEdgeFade}</option>
              <option value="32">{t.edgeFadePixels(32)}</option>
              <option value="64">{t.edgeFadePixels(64)}</option>
              <option value="96">{t.edgeFadePixels(96)}</option>
            </select>
          </div>

          <div className="p-2 bg-neutral-700/70 rounded-md">
            <label className="block text-xs text-neutral-200 mb-1" htmlFor="ga-grid-order-inline">
              {t.sweepOrder}
            </label>
            <select
              id="ga-grid-order-inline"
              value={order}
              onChange={handleOrderChange}
              className="w-full p-2 border border-neutral-600 rounded-md bg-neutral-800 text-white text-sm focus:ring-[#B3D9FF]"
              disabled={isSubmitting || sweepMode === 'overlapFromCenter'}
            >
              <option value="centerOut">{t.centerOut}</option>
              <option value="rowMajor">{t.rowMajor}</option>
            </select>
          </div>

          <div className="flex items-center justify-between p-2 bg-neutral-700/70 rounded-md">
            <span className="text-xs text-neutral-200">{t.centerProbeEven}</span>
            <Switch
              checked={includeCenterProbeForEven}
              onCheckedChange={setIncludeCenterProbeForEven}
              labelRight={t.on}
              labelLeft={t.off}
              disabled={isSubmitting || sweepMode === 'overlapFromCenter'}
            />
          </div>

          <div className="flex items-center justify-between p-2 bg-neutral-700/70 rounded-md">
            <span className="text-xs text-neutral-200">{t.tilePreviewOverlay}</span>
            <div className="flex items-center gap-2">
              {previewZoneCount > 0 && (
                <span className="text-[11px] text-neutral-300 tabular-nums">
                  {Math.min(previewActiveIndex + 1, previewZoneCount)} / {previewZoneCount}
                </span>
              )}
              <Switch
                checked={previewEnabled}
                onCheckedChange={handlePreviewEnabledChange}
                labelRight={t.on}
                labelLeft={t.off}
                disabled={isSubmitting}
              />
            </div>
          </div>

          <div className="p-2 bg-neutral-700/70 rounded-md text-xs text-neutral-300">
            {gridMetrics ? (
              <div className="space-y-1">
                <p>
                  {t.tiles}: <span className="text-white font-semibold">{gridMetrics.frameCount}</span>
                </p>
                <p>
                  {t.credits}:{' '}
                  {gridMetrics.estimatedCredits === null ? (
                    <span className="text-neutral-400">{t.creditsUnavailable}</span>
                  ) : (
                    <>
                      <span className="text-white font-semibold">{gridMetrics.estimatedCredits.toFixed(2)}</span>{' '}
                      <span className="text-neutral-400">({generationModelId})</span>
                    </>
                  )}
                </p>
              </div>
            ) : (
              t.gridSizeError
            )}
          </div>

          {error && (
            <div className="rounded-md border border-red-700/50 bg-red-950/40 px-2 py-2 text-xs text-red-300">
              {error}
            </div>
          )}
        </div>

        <div className="px-3 py-2 border-t border-neutral-700/80 bg-neutral-900/40 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 text-xs font-medium text-neutral-300 bg-neutral-700 hover:bg-neutral-600 rounded-md focus:outline-none focus:ring-2 focus:ring-[#B3D9FF]"
            disabled={isSubmitting}
          >
            {t.cancel}
          </button>
          <button
            type="submit"
            className="inline-flex min-w-[100px] items-center justify-center gap-2 px-3 py-1.5 text-xs font-medium text-white bg-neutral-600 hover:bg-neutral-500 rounded-md focus:outline-none focus:ring-2 focus:ring-[#B3D9FF] disabled:cursor-not-allowed disabled:opacity-70"
            disabled={isSubmitting}
          >
            {isSubmitting && <Loader2 size={14} className="animate-spin" />}
            {t.buildAndRun}
          </button>
        </div>
      </form>
    </div>
  );
};

export default React.memo(GridGenerationInlinePanel);
